import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import BranchSelect from "../../components/ui/BranchSelect";
import {
  useEvaluationReport,
  useTemplates,
} from "../../api/queries/performanceQueries";
import { useDepartments } from "../../api/queries/organizationQueries";
import useCurrentUser from "../../hooks/useCurrentUser";
import { getApiErrorMessage } from "../../utils/apiError";
import { currentPeriod } from "../../utils/dateTime";
import { EVALUATION_STATUS } from "./components/evaluationUtils";
import "./performance.css";
import usePermissions from "../../hooks/usePermissions";

const EMPTY = [];
const round = (n) =>
  n === null || n === undefined ? "—" : Math.round(Number(n) * 100) / 100;

const Tile = ({ label, value, hint }) => (
  <div className="report-tile">
    <div className="approval-meta-label">{label}</div>
    <div className="report-tile-value tabular-nums">{value ?? 0}</div>
    {hint && <div className="fs-070 text-slate-500">{hint}</div>}
  </div>
);

/** Rating-band distribution: one hue, direct count labels, hover for the share. */
const BandBars = ({ bands }) => {
  const max = Math.max(1, ...bands.map((b) => b.count || 0));
  const total = bands.reduce((s, b) => s + (b.count || 0), 0);
  if (!bands.length)
    return (
      <p className="fs-085 text-slate-500 mb-0">No rated evaluations yet.</p>
    );
  return (
    <ul className="band-bars" aria-label="Evaluations per rating band">
      {bands.map((b) => {
        const share = total ? Math.round(((b.count || 0) / total) * 100) : 0;
        return (
          <li
            key={b.band || "none"}
            title={`${b.band || "No band"}: ${b.count} (${share}%)`}
          >
            <span className="band-name">{b.band || "No band"}</span>
            <span className="band-track">
              <span
                className="band-fill"
                style={{ "--w": `${((b.count || 0) / max) * 100}%` }}
              ></span>
            </span>
            <span className="band-value tabular-nums">
              {b.count} <span className="text-slate-500">· {share}%</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
};

/** Admin/HR: evaluation results for a year, by status, rating band and branch. */
const EvaluationReportsPage = () => {
  const user = useCurrentUser();
  // HR-level (performance with COMPANY scope); role names grant nothing
  const isCompanyWide = usePermissions().companyWide("performance");
  const thisYear = currentPeriod().year;
  const [year, setYear] = useState(String(thisYear));
  const [templateCode, setTemplateCode] = useState("");
  const [branchId, setBranchId] = useState("");
  const [departmentId, setDepartmentId] = useState("");

  const query = useEvaluationReport({
    year,
    templateCode,
    branchId,
    departmentId,
  });
  const templates = useTemplates();
  const { data: departments = EMPTY } = useDepartments(user.companyId);
  const r = query.data;
  const codes = useMemo(
    () => [...new Set((templates.data || EMPTY).map((t) => t.code))].sort(),
    [templates.data],
  );

  if (!isCompanyWide) {
    return (
      <Layout>
        <div className="container-fluid py-4 px-lg-4">
          <div className="builder-card approval-empty">
            <i className="bi bi-shield-lock"></i>Only Admin and HR can view
            evaluation reports.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="mb-4">
          <Link
            to="/performance/evaluations"
            className="text-slate-500 fs-080 text-decoration-none"
          >
            <i className="bi bi-arrow-left"></i> Evaluations
          </Link>
          <h1 className="fw-bold text-dark m-0 page-title">
            Evaluation Reports
          </h1>
          <p className="text-muted small mb-0">
            Results by status, rating band and branch (year of the period end).
          </p>
        </div>

        <div className="perf-filters mb-3">
          <select
            className="status-select"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            aria-label="Year"
          >
            {[thisYear, thisYear - 1, thisYear - 2, thisYear - 3].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <select
            className="status-select"
            value={templateCode}
            onChange={(e) => setTemplateCode(e.target.value)}
            aria-label="Form"
          >
            <option value="">All forms</option>
            {codes.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <div className="perf-filter-branch">
            <BranchSelect
              value={branchId}
              onChange={(v) => setBranchId(v || "")}
              id="report-branch"
            />
          </div>
          <select
            className="status-select"
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
            aria-label="Department"
          >
            <option value="">All departments</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.departmentName}
              </option>
            ))}
          </select>
          {query.isFetching && (
            <span
              className="spinner-border spinner-border-sm text-primary"
              role="status"
              aria-label="Loading"
            ></span>
          )}
        </div>

        {query.isError && (
          <div className="alert alert-danger fs-085">
            {getApiErrorMessage(query.error, "Could not load the report.")}
          </div>
        )}

        {r && (
          <>
            <div className="report-tiles mb-3">
              <Tile label="Evaluations" value={r.total} />
              <Tile label="Completed" value={r.completed} />
              <Tile label="Contested" value={r.contested} />
              <Tile
                label="Average score"
                value={round(r.averageScore)}
                hint="of completed, out of 100"
              />
            </div>

            <div className="row g-3">
              <div className="col-lg-6">
                <section className="builder-card h-100">
                  <h2 className="builder-card-title">Rating bands</h2>
                  <BandBars bands={r.ratingBands || EMPTY} />
                </section>
              </div>
              <div className="col-lg-6">
                <section className="builder-card h-100">
                  <h2 className="builder-card-title">By status</h2>
                  <ul className="report-status-list">
                    {Object.entries(r.byStatus || {}).map(([status, n]) => (
                      <li key={status}>
                        <span
                          className={`status-pill status-pill--${EVALUATION_STATUS[status]?.tone || "gray"}`}
                        >
                          {EVALUATION_STATUS[status]?.label || status}
                        </span>
                        <strong className="tabular-nums">{n}</strong>
                      </li>
                    ))}
                  </ul>
                </section>
              </div>
            </div>

            <section className="builder-card mt-3">
              <h2 className="builder-card-title">By branch</h2>
              <div className="table-responsive">
                <table className="table table-sm mb-0 perf-table">
                  <thead>
                    <tr>
                      <th>Branch</th>
                      <th className="text-end">Evaluations</th>
                      <th className="text-end">Average score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(r.byBranch || EMPTY).map((b) => (
                      <tr key={b.branchId || "none"}>
                        <td>{b.branchName || "No branch"}</td>
                        <td className="text-end tabular-nums">{b.count}</td>
                        <td className="text-end tabular-nums">
                          {round(b.averageScore)}
                        </td>
                      </tr>
                    ))}
                    {(r.byBranch || EMPTY).length === 0 && (
                      <tr>
                        <td colSpan={3} className="text-slate-500">
                          No data.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </Layout>
  );
};

export default EvaluationReportsPage;
